'use client';

import {
  AccordionPrimitive,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';

import { images } from '~/landing/assets';

import { Badge } from '../components/badge';
import { Title } from '../components/title';
import { Typography } from '../components/typography';

const features = [
  {
    id: 'app',
    marquee: {
      variant: 'badge',
      items: [
        { key: 'onlineSubscription', variant: 'info' },
        { key: 'apiSubscription', variant: 'info' },
        { key: 'electronicSignature', variant: 'info' },
        { key: 'individualCollectiveSubscription', variant: 'info' },
        { key: 'attachmentDownload', variant: 'info' },
        { key: 'contractManagement', variant: 'neutral' },
        { key: 'subscriptionTracking', variant: 'neutral' },
        { key: 'operationsManagement', variant: 'neutral' },
        { key: 'portfolioAnalytics', variant: 'neutral' },
        { key: 'controlsValidations', variant: 'success' },
        { key: 'documentGeneration', variant: 'success' },
        { key: 'ocsValidation', variant: 'success' },
      ],
    },
  },
  { id: 'core' },
  {
    id: 'integration',
    marquee: {
      variant: 'logo',
      items: [
        'hubspot',
        'informatica',
        'kapia',
        'mailjet',
        'mulesoft',
        'salesforce',
        'yousign',
      ],
    },
  },
] as const;

type FeatureVisualizerProps = {
  features: typeof features;
  openFeature: string;
  setOpenFeature: (id: string) => void;
  interactive?: boolean;
  className?: string;
};

/**
 * FeatureVisualizer = stacked 3D squares
 */
const FeatureVisualizer = ({
  features,
  openFeature,
  setOpenFeature,
  className,
  interactive = true,
}: FeatureVisualizerProps) => {
  const t = useTranslations('landing.polyseme.features');
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      style={{
        perspective: 1000,
        // @ts-expect-error -- CSS variable
        ['--depth-multiplier']: '80px',
      }}
      onMouseEnter={() => interactive && setIsHovered(true)}
      onMouseLeave={() => interactive && setIsHovered(false)}
    >
      {features.map(({ id }, index) => {
        const total = features.length;
        const baseSpacing = isHovered ? 16 : 0;
        const offset = baseSpacing * (total / 2 - index - 0.5);
        return (
          <div
            key={id}
            aria-label={t('selectFeature', {
              feature: t(`featureNames.${id}`),
            })}
            className={cn(
              `absolute aspect-square w-1/2 cursor-default rounded-3xl shadow-xl transition-all duration-300`,
              openFeature === id ? 'bg-indigo-400' : 'bg-white'
            )}
            role={interactive ? 'button' : undefined}
            style={{
              transform: `
                rotateX(60deg)
                rotateZ(45deg)
                translateZ(calc(-1 * var(--depth-multiplier) * ${index} + ${offset}px))
              `,
              zIndex: total - index,
            }}
            onClick={interactive ? () => setOpenFeature(id) : undefined}
          />
        );
      })}
    </div>
  );
};

export const FeaturesSection = () => {
  const t = useTranslations('landing.polyseme.features');
  const sectionRef = useRef<HTMLElement>(null);
  const [openFeature, setOpenFeature] = useState<string>('app');

  const handleValueChange = (val: string) => {
    // If user clicks on open item, val will be empty - open the next one
    if (!val) {
      const currentIndex = features.findIndex((f) => f.id === openFeature);
      const nextIndex = (currentIndex + 1) % features.length;
      const nextFeature = features[nextIndex];
      if (nextFeature) {
        setOpenFeature(nextFeature.id);
      }
    } else {
      setOpenFeature(val);
    }

    if (sectionRef.current && window.innerWidth < 1024) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="mx-auto flex w-full max-w-screen-xl flex-col overflow-x-hidden px-4 py-[60px] lg:px-0"
      id="features"
    >
      <Title
        as="h2"
        className="mb-10 whitespace-pre-wrap text-center"
        variant="title-2"
      >
        {t('title')}
      </Title>

      <div className="grid overflow-x-hidden rounded-3xl bg-gray-100 p-10 lg:min-h-[550px] lg:grid-cols-2">
        {/* Accordion (Left side) */}
        <AccordionPrimitive
          collapsible
          className="flex max-w-full flex-col gap-4 overflow-hidden"
          type="single"
          value={openFeature}
          onValueChange={handleValueChange}
        >
          {features.map((feature) => (
            <AccordionItem
              key={feature.id}
              className="max-w-full overflow-hidden border-b border-gray-200 pb-4 last:border-0"
              value={feature.id}
            >
              <AccordionTrigger className="flex w-full items-center justify-between gap-2 text-left hover:no-underline [&>svg]:size-8 [&[data-state=open]>svg]:text-primary">
                <Title as="h3" variant="title-3">
                  {t.rich(`${feature.id}.title`, {
                    highlight: (children) => (
                      <span className="text-primary">{children}</span>
                    ),
                  })}
                </Title>
              </AccordionTrigger>
              <AccordionContent className="mt-4 max-w-full overflow-hidden">
                <Typography className="whitespace-pre-wrap" variant="body">
                  {t.rich(`${feature.id}.description`, {
                    strong: (children) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  })}
                </Typography>

                {'marquee' in feature && feature.id === 'app' && (
                  <div className="mt-6">
                    <Marquee pauseOnHover speed={100}>
                      {feature.marquee.items.map((item, index) => (
                        <div key={index} className="mx-1">
                          <Badge size="lg" variant={item.variant}>
                            {t(`${feature.id}.badges.${item.key}`)}
                          </Badge>
                        </div>
                      ))}
                    </Marquee>
                  </div>
                )}
                {'marquee' in feature && feature.id === 'integration' && (
                  <div className="mt-6">
                    <Marquee pauseOnHover speed={100}>
                      {feature.marquee.items.map((item, index) => (
                        <Image
                          key={index}
                          alt={item}
                          className="mx-3 h-[42px] w-auto object-contain"
                          height={42}
                          src={images.polyseme.integrations[item]}
                          width={128}
                        />
                      ))}
                    </Marquee>
                  </div>
                )}

                {/* Mobile-only, non-clickable */}
                <FeatureVisualizer
                  className="mx-auto mt-6 h-72 w-full -translate-y-20 sm:w-3/4 md:h-96 lg:hidden"
                  features={features}
                  interactive={false}
                  openFeature={openFeature}
                  setOpenFeature={setOpenFeature}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </AccordionPrimitive>

        {/* Desktop right column, clickable */}
        <FeatureVisualizer
          className="group hidden -translate-y-24 lg:flex"
          features={features}
          interactive={true}
          openFeature={openFeature}
          setOpenFeature={setOpenFeature}
        />
      </div>
    </section>
  );
};
